import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from './Icon';

/**
 * A bounded scroll area that makes its overflow obvious: a fade at the bottom edge and a sticky
 * "more below" strip while there is content past the fold (both vanish at the end).
 */
export function ScrollBox({ maxHeight = 440, itemLabel = 'items', count, children }: { maxHeight?: number; itemLabel?: string; count: number; children: ComponentChildren }) {
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(0);
  const update = () => {
    const el = ref.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
    if (remaining <= 4) {
      setMore(0);
      return;
    }
    // estimate how many rows are still below from the average row height
    const rows = el.querySelectorAll('tbody tr, li').length || count;
    const rowH = rows ? el.scrollHeight / rows : 40;
    setMore(Math.max(1, Math.round(remaining / rowH)));
  };
  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count]);
  return (
    <div class={`scrollbox ${more > 0 ? 'has-more' : ''}`} style={{ maxHeight: `${maxHeight}px` }} ref={ref} onScroll={update}>
      {children}
      {more > 0 && (
        <button type="button" class="scrollbox-more" onClick={() => ref.current?.scrollBy({ top: maxHeight * 0.8, behavior: 'smooth' })}>
          <Icon name="chevronDown" size={14} /> {more} more {itemLabel} below — scroll for the rest
        </button>
      )}
    </div>
  );
}
